import type {
  Media,
  Variant,
} from '../../../../lib/flotiq-client/types/api-response';
import type { UseAppForm } from '../../../form';

import { useStore } from '@tanstack/react-form';
import { useCallback, useMemo } from 'react';

import { deepAssignKeyValue, getTestProps } from '../../../../lib/helpers';

import VariantCard from '../../../../components/VariantCard/VariantCard';

import { useFieldContext, useFormContext } from '../../../form';

interface VariantFieldProps {
  /**
   * Media data
   */
  media: Media;
  /**
   * Function to open the variant modal
   */
  openVariantModal: (variant: Variant, idx: number, errors?: object) => void;
  /**
   * Function to delete the variant
   */
  deleteVariant: (idx: number) => void;
  /**
   * Index of the variant in the list
   */
  idx: number;
  /**
   * If form is disabled
   */
  disabled?: boolean;
  /**
   * Test ID for testing purposes
   */
  testId?: string;
}

const VariantField = ({
  media,
  openVariantModal,
  deleteVariant,
  idx,
  disabled,
  testId,
}: VariantFieldProps) => {
  const form = useFormContext() as unknown as UseAppForm;
  const field = useFieldContext<Variant>();

  const errors = useStore(form.store, ({ fieldMeta }) => {
    const variantErrors: Record<string, any[]> = {};
    Object.entries(fieldMeta as { [key: string]: { errors: any[] } })
      ?.filter(
        ([key, { errors }]) => key.startsWith(field.name) && errors.length,
      )
      .forEach(([key, { errors }]) => {
        deepAssignKeyValue(key, errors, variantErrors);
      });
    return variantErrors.variants?.[idx];
  });

  const variant = useMemo(() => field.state.value, [field.state.value]);

  const isNew = media?.variants
    ? !media.variants.find(
        (oldVariant: Variant) => oldVariant.name === variant.name,
      )
    : true;

  const onDelete = useCallback(() => deleteVariant(idx), [deleteVariant, idx]);

  const onEdit = () => {
    openVariantModal(variant, idx, errors);
  };

  return (
    <VariantCard
      variant={field.state.value}
      media={media}
      onEdit={isNew ? onEdit : undefined}
      onDelete={onDelete}
      errors={errors}
      isNew={isNew}
      disabled={disabled}
      {...getTestProps(testId, variant.name, 'testId')}
    />
  );
};

export default VariantField;
